import { BirthingCenterAdmissionData } from '../../gyn/encounters/types/admission'
import { LaborEntry, LaborEntryType } from './laborEntry'

// ─── §5.1 Full Delivery Record — additional types ─────────────────────────────

export type FetalPresentation = 'vertex' | 'breech' | 'transverse' | 'face' | 'brow' | 'compound'
export type CordManagement = 'immediate' | 'delayed'
export type MeconiumPresence = 'none' | 'thin' | 'thick'
export type PlacentaCompleteness = 'complete' | 'incomplete' | 'retained'
export type LacerationDegree = 'none' | 'first' | 'second' | 'third' | 'fourth' | 'periurethral'
export type VitaminKRoute = 'im' | 'oral'

// §5.2 Delivery time log — all fields in minutes
export interface DeliveryTimeLog {
  /** Pulled from §4.3 intrapartum time log — auto-populated, provider-adjustable */
  activeManagementMinutes?: number
  deliveryAttendanceMinutes?: number
  lacerationRepairMinutes?: number
  immediatePostpartumMinutes?: number
  newbornAssessmentMinutes?: number
  documentationMinutes?: number
  /** Auto-calculated sum of all fields above */
  totalDeliveryMinutes?: number
  updatedOn?: number
  updatedBy?: string
}

// §5.1 Full delivery record — additional fields beyond §4 minimum delivery report
export interface FullDeliveryRecord {
  deliveringProviderId?: string
  fetalPresentation?: FetalPresentation
  cordManagement?: CordManagement
  /** Only set when cordManagement === 'delayed' */
  dccDurationSeconds?: number
  meconiumPresence?: MeconiumPresence
  estimatedBloodLossDeliveryMl?: number
  /** Auto-calculated: delivery EBL + any previously documented intrapartum EBL */
  totalEblMl?: number
  placentaDeliveryTime?: number
  placentaCompleteness?: PlacentaCompleteness
  apgar5Minute?: ApgarScore
  /** Tier 2 — prompted when apgar5Minute.total ≤ 6 */
  apgar10Minute?: ApgarScore
  birthWeightGrams?: number
  skinToSkinInitiated?: boolean
  skinToSkinTime?: number
  laceration?: LacerationDegree
  lacerationRepairPerformed?: boolean
  /** Clock time the laceration repair was performed (Unix ms) — distinct from lacerationRepairMinutes (duration) */
  lacerationRepairTime?: number
  eyeProphylaxisAdministered?: boolean
  eyeProphylaxisDocumentedAt?: number
  vitaminKAdministered?: boolean
  vitaminKAdministeredAt?: number
  vitaminKRoute?: VitaminKRoute
  icd10Codes?: string[]
  deliveryTimeLog?: DeliveryTimeLog
  savedAt?: number
  lastModifiedAt?: number
}

// ─── §5.4 Neonatal Record ──────────────────────────────────────────────────────

export type CordBloodBankingDecision = 'elected' | 'declined' | 'undecided'

export interface CordBloodBankingRecord {
  bankingDecision?: CordBloodBankingDecision
  collectionKitPresent?: boolean
  collectionPerformed?: boolean
  collectionNotPerformedReason?: string
  bankingCompanyName?: string
  collectionTime?: number
  chainOfCustodyConfirmed?: boolean
}

export type CordBloodSampleType = 'arterial' | 'venous' | 'both'

// Cord blood gas values stored inline on NeonatalRecord.
// Results also appear in the neonatal lab quickview as a synthesized row.
export interface CordBloodGasRecord {
  ordered?: boolean
  indication?: string
  sampleType?: CordBloodSampleType
  ph?: number
  pco2?: number
  po2?: number
  baseExcess?: number
  bicarbonate?: number
  acknowledgedAt?: number
  acknowledgedBy?: string
}

export interface NewbornPKURecord {
  screenOrdered?: boolean
  screenOrderedDate?: string
  collectionDate?: string
  collectionTime?: number
  screenKitId?: string
  collectedBy?: string
  resultsReceived?: boolean
  resultsDate?: string
  resultsAbnormal?: boolean
  abnormalFollowUpPlan?: string
}

// Stored at LABOR_CHARTING/{assessmentId}.neonatalRecord
export interface NeonatalRecord {
  cordBloodBanking?: CordBloodBankingRecord
  cordBloodGas?: CordBloodGasRecord
  pkuRecord?: NewbornPKURecord
  createdAt?: number
  updatedAt?: number
}

// §6.5: Per-calendar-day documentation confirmation for subsequent-day billing.
// Keyed by YYYY-MM-DD (local time of each labor day after the admission day).
export interface SubsequentDayNote {
  vitalsConfirmed: boolean
  fhrConfirmed: boolean
  timeLogConfirmed: boolean
  confirmedOn?: number
  confirmedBy?: string
}

// §4.0: Homebirth activation record — created when provider confirms labor onset outside
// a birthing center. Serves as the labor start anchor for the homebirth episode.
export interface HomebirthOnsetRecord {
  /** Provider-confirmed onset time (Unix ms) */
  onsetAt: number
  /** userId of the provider who confirmed */
  confirmedBy: string
  /** Wall-clock time when the provider tapped Confirm (Unix ms) */
  confirmedAt: number
}

// §4.1.2: Minimum delivery report — six fields required immediately after birth.
// Named MinimumDeliveryType to avoid collision with the existing assessment.ts DeliveryType.
export type MinimumDeliveryType =
  | 'spontaneous-vaginal'
  | 'operative-vaginal'
  | 'cesarean'
  | 'precipitous'
export type BabySex = 'male' | 'female' | 'indeterminate'
export type ApgarComponent = 0 | 1 | 2

export interface ApgarScore {
  heartRate: ApgarComponent
  respirations: ApgarComponent
  tone: ApgarComponent
  reflex: ApgarComponent
  color: ApgarComponent
  /** Auto-calculated sum of the five components */
  total: number
}

export interface MinimumDeliveryData {
  /** Unix ms time of birth as entered by provider */
  timeOfBirth: number
  deliveryType?: MinimumDeliveryType
  babySex?: BabySex
  apgar1Minute?: ApgarScore
  resuscitationRequired?: boolean
  /** Required when resuscitationRequired === true */
  resuscitationDescription?: string
  immediateConcernFlag?: boolean
  /** Required when immediateConcernFlag === true — routes urgent flag to billing team */
  immediateConcernDescription?: string
  /** Unix ms when provider first saved — after this point, amendment workflow applies */
  savedAt?: number
}

// §4.3: Intrapartum time log — attendance periods and total for AMA code documentation.
export interface AttendancePeriod {
  id: string
  startAt: number
  endAt?: number
  addedBy: string
}

export interface LaborIntrapartumTimeLog {
  attendancePeriods: AttendancePeriod[]
  /** Auto-calculated sum of completed attendance periods in minutes */
  totalIntrapartumMinutes?: number
  updatedOn?: number
  updatedBy?: string
}

export interface LaborCharting {
  entries: Record<string, LaborEntry<LaborEntryType>>
  /** Birthing-center activation: presence indicates patient was admitted */
  admission?: BirthingCenterAdmissionData
  // §6.5: Subsequent-day billing confirmation state per calendar date.
  subsequentDayNotes?: Record<string, SubsequentDayNote>
  // §4.0: Homebirth activation record — alternative to birthing-center admission.
  homebirthOnset?: HomebirthOnsetRecord
  // §4.1.1: System-only audit timestamp for Report Birth button tap.
  // Never displayed in the clinical record. Never editable by anyone including admin.
  reportBirthTappedAt?: number
  // §4.1.2: Minimum delivery report (six fields).
  minimumDeliveryReport?: MinimumDeliveryData
  // §4.3: Intrapartum time log.
  intrapartumTimeLog?: LaborIntrapartumTimeLog
  // §5.1: Full delivery record — extended fields beyond the §4 minimum report.
  fullDeliveryRecord?: FullDeliveryRecord
  // §5.4: Neonatal record — cord blood, PKU, created automatically on first delivery save.
  neonatalRecord?: NeonatalRecord
}

// Returns the set of unique calendar dates (YYYY-MM-DD local time) spanned by
// labor entries, sorted ascending. Used to detect midnight crossings.
export const getLaborCalendarDates = (
  entries: LaborEntry<LaborEntryType>[],
  admittedAt?: number,
): string[] => {
  const dates = new Set<string>()
  if (admittedAt) {
    const d = new Date(admittedAt)
    dates.add(
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`,
    )
  }
  for (const entry of entries) {
    const d = new Date(entry.startedAt)
    dates.add(
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`,
    )
  }
  return Array.from(dates).sort()
}

// Returns true if a subsequent-day note has all required fields confirmed.
export const isSubsequentDayComplete = (note: SubsequentDayNote | undefined): boolean =>
  (note?.vitalsConfirmed ?? false) &&
  (note?.fhrConfirmed ?? false) &&
  (note?.timeLogConfirmed ?? false)
